import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../config";
import { Bell, Check, CheckCheck, UserPlus, Search, CreditCard, ShieldAlert, X, Trash2, ClipboardList } from "lucide-react";
import { playNotificationSound, getStoredSound } from "../../utils/notificationSounds";

const ICON_MAP = {
  new_lead: UserPlus,
  scan_completed: Search,
  new_registration: UserPlus,
  payment_received: CreditCard,
  security_alert: ShieldAlert,
  daily_recap: ClipboardList,
  system: Bell,
};

const COLOR_MAP = {
  new_lead: "text-blue-500",
  scan_completed: "text-emerald-500",
  new_registration: "text-purple-500",
  payment_received: "text-amber-500",
  security_alert: "text-red-500",
  daily_recap: "text-pink-500",
  system: "text-[var(--color-text-muted)]",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "práve teraz";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export const NotificationBell = ({ variant = "dark" }) => {
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const prevUnread = useRef(0);

  // Poll unread count every 30s
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/push-notifications/unread-count`, { withCredentials: true });
        const newUnread = r.data.unread || 0;
        if (newUnread > prevUnread.current && prevUnread.current >= 0) {
          playNotificationSound(getStoredSound());
        }
        prevUnread.current = newUnread;
        setUnread(newUnread);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/push-notifications?limit=20`, { withCredentials: true });
      setNotifications(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch {}
    setLoading(false);
  };

  const handleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/push-notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  const markRead = async (createdAt) => {
    try {
      await axios.put(`${API}/push-notifications/${encodeURIComponent(createdAt)}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.created_at === createdAt ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const clearRead = async () => {
    try {
      await axios.delete(`${API}/push-notifications/clear-all`, { withCredentials: true });
      setNotifications(prev => prev.filter(n => !n.read));
    } catch {}
  };

  const isDark = variant === "dark";

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-wrapper">
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className={`relative p-2 rounded-lg transition-colors ${isDark ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-[#a855f7] text-white text-[10px] font-bold rounded-full" data-testid="notification-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className={`absolute right-0 top-full mt-2 w-96 max-h-[480px] overflow-hidden rounded-lg shadow-2xl border z-50 ${isDark ? 'bg-[var(--color-surface)] border-[var(--color-border)]' : 'bg-white border-gray-200'}`} data-testid="notification-panel">
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[var(--color-border)]' : 'border-gray-200'}`}>
            <h3 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Notifikácie {unread > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#a855f7] text-white text-[10px] font-bold rounded-full">{unread}</span>}
            </h3>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAllRead} className={`p-1.5 rounded transition-colors ${isDark ? 'text-[var(--color-text-muted)] hover:text-emerald-400 hover:bg-white/5' : 'text-gray-400 hover:text-emerald-600 hover:bg-gray-100'}`} title="Označiť všetky ako prečítané" data-testid="mark-all-read">
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button onClick={clearRead} className={`p-1.5 rounded transition-colors ${isDark ? 'text-[var(--color-text-muted)] hover:text-red-400 hover:bg-white/5' : 'text-gray-400 hover:text-red-600 hover:bg-gray-100'}`} title="Vymazať prečítané" data-testid="clear-read">
                <Trash2 className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} className={`p-1.5 rounded transition-colors ${isDark ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[400px]">
            {loading && notifications.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-[var(--color-text-muted)]' : 'text-gray-400'}`}>
                <Bell className="w-6 h-6 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Načítavam...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-[var(--color-text-muted)]' : 'text-gray-400'}`}>
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Žiadne notifikácie</p>
              </div>
            ) : (
              notifications.map((n, i) => {
                const Icon = ICON_MAP[n.type] || Bell;
                const color = COLOR_MAP[n.type] || "text-[var(--color-text-muted)]";
                return (
                  <div
                    key={i}
                    onClick={() => !n.read && markRead(n.created_at)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b transition-colors ${
                      isDark
                        ? `border-[var(--color-border)]/50 ${n.read ? 'opacity-50' : 'bg-white/[0.02] hover:bg-white/5'}`
                        : `border-gray-100 ${n.read ? 'opacity-50' : 'bg-blue-50/30 hover:bg-blue-50'}`
                    }`}
                    data-testid={`notification-item-${i}`}
                  >
                    <div className={`mt-0.5 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>{n.title}</p>
                      <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-[var(--color-text-muted)]' : 'text-gray-500'}`}>{n.message}</p>
                      <p className={`text-[10px] mt-1 ${isDark ? 'text-[var(--color-text-muted)]' : 'text-gray-400'}`}>{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 mt-1.5 rounded-full bg-[#a855f7] flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
